// Types
import { Me } from 'api/auth/types/Me';
import { UserRole, StoreStatus } from 'api/graphql-global-types';
// Constants
import { STORE_USER_FIRST_LOGIN } from 'constants/auth';

type User = Me | undefined;

export const getUserFullName = (user: User): string => {
  return `${user?.me?.firstName || ''} ${user?.me?.lastName || ''}`.trim();
};

export const isUserAthlete = (user: User): boolean => {
  return user?.me.role === UserRole.Athlete;
};

export const isUserOrganization = (user: User): boolean => {
  return user?.me.role === UserRole.Organization;
};

export const isUserContentCreator = (user: User): boolean => {
  return user?.me.role === UserRole.ContentCreator;
};

export const isActiveStore = (user: User): boolean => {
  return user?.me.status === StoreStatus.Active;
};

export const isOnboardedStore = (user: User): boolean => {
  return !!user?.me.isOnboarded;
};

export const isUserCustomer = (user: User): boolean => {
  return user?.me.role === UserRole.Customer;
};

export const isStoreOwner = (user: User, storeId?: string | null): boolean => {
  if (!user?.me?.id || !storeId) return false;
  return user.me.id === storeId;
};

export const isStoreRole = (userRole?: UserRole | null): boolean => {
  if (!userRole) return false;

  return [
    UserRole.Athlete,
    UserRole.Organization,
    UserRole.ContentCreator,
  ].includes(userRole);
};

export const isStoreOrganization = (
  store?: { role?: UserRole | null } | null
): boolean => {
  return store?.role === UserRole.Organization;
};

export const isAdminRole = (userRole?: UserRole | null): boolean => {
  if (!userRole) return false;

  return [UserRole.Admin].includes(userRole);
};

export const mapStreamerRoleToLabel = (role: UserRole): string => {
  switch (role) {
    case UserRole.Athlete:
      return 'Athlete';
    case UserRole.Organization:
      return 'Organization';
    case UserRole.ContentCreator:
      return 'Content Creator';
    default:
      return '';
  }
};

export const getUserRoleLabel = (role: UserRole): string => {
  switch (role) {
    case UserRole.Athlete:
      return 'Professional Athlete';
    case UserRole.ContentCreator:
      return 'Content Creator';
    case UserRole.Customer:
      return 'Fan';
    case UserRole.ManagementFirm:
      return 'Management Firm';
    case UserRole.VideoEditor:
      return 'Video Editor';
    case UserRole.Admin:
      return 'Admin';
    case UserRole.Agency:
      return 'Agency';
    case UserRole.Association:
      return 'Association';
    case UserRole.Charity:
      return 'Charity';
    case UserRole.Coach:
      return 'Coach';
    case UserRole.Designer:
      return 'Designer';
    case UserRole.Guest:
      return 'Guest';
    case UserRole.Gym:
      return 'Gym';
    case UserRole.Moderator:
      return 'Moderator';
    case UserRole.Organization:
      return 'Organization';
    case UserRole.Team:
      return 'Team';
    case UserRole.Trainer:
      return 'Trainer';
    default:
      return role;
  }
};

export const handleFirstLogin = (userRole?: UserRole | null): void => {
  if (isStoreRole(userRole)) {
    localStorage.setItem(STORE_USER_FIRST_LOGIN, 'true');
  }
};
